// src/services/xmlExportService.js
import JSZip from "jszip";

/**
 * Utilitário para disparar download direto no navegador via Blob,
 * sem disparar bloqueadores de pop-up do navegador.
 */
export const dispararDownloadBlob = (blob, nomeArquivo) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

/**
 * Extrai metadados oficiais contábeis de dentro do próprio XML autorizado pela SEFAZ.
 */
export const extrairMetadadosXml = (xmlString, fallbackId = "") => {
  if (!xmlString || typeof xmlString !== "string") {
    return {
      numero: "000000",
      serie: "1",
      chave: fallbackId || "SEM_CHAVE",
      dhEmi: "",
      vNF: "0.00",
      nProt: "",
      cStat: "",
      xMotivo: "",
      nomeArquivo: `NFCe_${fallbackId || "nota"}.xml`,
    };
  }

  const nNFMatch = xmlString.match(/<nNF>(\d+)<\/nNF>/);
  const serieMatch = xmlString.match(/<serie>(\d+)<\/serie>/);
  const chNFMatch =
    xmlString.match(/<chNFe>(\d{44})<\/chNFe>/) ||
    xmlString.match(/Id="NFe(\d{44})"/);
  const dhEmiMatch = xmlString.match(/<dhEmi>(.*?)<\/dhEmi>/);
  const vNFMatch = xmlString.match(/<vNF>(.*?)<\/vNF>/);
  const nProtMatch = xmlString.match(/<nProt>(\d+)<\/nProt>/);
  const cStatMatch = xmlString.match(/<cStat>(\d+)<\/cStat>/);
  const xMotivoMatch = xmlString.match(/<xMotivo>(.*?)<\/xMotivo>/);

  const rawNumero = nNFMatch ? nNFMatch[1] : "";
  const numeroFormatado = rawNumero ? rawNumero.padStart(6, "0") : "000000";
  const chave = chNFMatch ? chNFMatch[1] : fallbackId || "SEM_CHAVE";
  const serie = serieMatch ? serieMatch[1] : "1";
  const vNF = vNFMatch ? Number(vNFMatch[1]).toFixed(2) : "0.00";
  const nProt = nProtMatch ? nProtMatch[1] : "";
  const dhEmi = dhEmiMatch ? dhEmiMatch[1] : "";
  const cStat = cStatMatch ? cStatMatch[1] : "";
  const xMotivo = xMotivoMatch ? xMotivoMatch[1] : "";

  // Nome padronizado corporativo: NFCe_000002_28260908855628000180650010000000021763092465.xml
  const nomeArquivo = `NFCe_${numeroFormatado}_${chave}.xml`;

  return {
    numero: numeroFormatado,
    rawNumero,
    serie,
    chave,
    dhEmi,
    vNF,
    nProt,
    cStat,
    xMotivo,
    nomeArquivo,
  };
};

/**
 * Busca o conteúdo XML oficial de uma nota pelo ID da NotaaS via nosso servidor provedor.
 */
export const obterXmlViaApi = async (notaId, baseUrl) => {
  if (!notaId) throw new Error("ID da nota fiscal ausente.");
  const url = `${baseUrl}/api/fiscal/xml/${notaId}`;

  const res = await fetch(url);
  if (!res.ok) {
    if (res.status === 422) {
      throw new Error(
        `Nota ${notaId} ainda está em processamento na SEFAZ (aguarde alguns instantes).`
      );
    }
    const errText = await res.text();
    throw new Error(`Falha ao obter XML (${res.status}): ${errText}`);
  }

  const xmlText = await res.text();
  if (!xmlText || (!xmlText.includes("<?xml") && !xmlText.includes("<nfeProc") && !xmlText.includes("<NFe"))) {
    throw new Error(`Retorno inválido ou vazio para a nota ${notaId}`);
  }

  return xmlText;
};

/**
 * Gera um arquivo CSV com encoding UTF-8 com BOM (\uFEFF) para abrir com perfeição no Excel.
 */
export const gerarCsvResumo = (itensRelatorio) => {
  const cabecalho = [
    "Item",
    "Número NFC-e",
    "Série",
    "Data/Hora Emissão",
    "Valor Total (R$)",
    "Chave de Acesso (44 dígitos)",
    "Protocolo SEFAZ",
    "Status SEFAZ",
    "Nome Arquivo XML",
  ];

  const linhas = [cabecalho.map((col) => `"${col}"`).join(";")];

  itensRelatorio.forEach((it, index) => {
    const dataFormatada = it.dhEmi
      ? new Date(it.dhEmi).toLocaleString("pt-BR")
      : "N/D";
    const linha = [
      index + 1,
      it.numero || "N/D",
      it.serie || "1",
      dataFormatada,
      it.vNF ? it.vNF.replace(".", ",") : "0,00",
      `'${it.chave || ""}`, // aspas simples inicial para o Excel não truncar o número de 44 dígitos
      it.nProt || "N/D",
      it.status || (it.cStat === "100" ? "Autorizada" : "Processada"),
      it.nomeArquivo || "",
    ];
    linhas.push(linha.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(";"));
  });

  // \uFEFF força o Excel a reconhecer UTF-8
  return "\uFEFF" + linhas.join("\r\n");
};

/**
 * Realiza o download de uma única nota com nome profissional sem abrir pop-ups bloqueados.
 */
export const baixarXmlIndividual = async ({ notaId, xmlDireto, dadosNota = {}, baseUrl }) => {
  try {
    let xmlContent = xmlDireto;
    if (!xmlContent && notaId) {
      xmlContent = await obterXmlViaApi(notaId, baseUrl);
    }

    if (!xmlContent) {
      throw new Error("Não foi possível recuperar o conteúdo XML da nota.");
    }

    const metadados = extrairMetadadosXml(xmlContent, notaId);
    const blob = new Blob([xmlContent], { type: "application/xml;charset=utf-8" });
    dispararDownloadBlob(blob, metadados.nomeArquivo);
    return { success: true, nomeArquivo: metadados.nomeArquivo };
  } catch (err) {
    console.error("Erro no download individual:", err);
    throw err;
  }
};

/**
 * Empacota todas as notas de um único lote em um arquivo .ZIP
 */
export const baixarLoteZip = async (lote, baseUrl, onProgress = () => {}) => {
  const notasValidas = (lote.retornoNotaaS || []).filter(
    (r) => (r.status >= 200 && r.status < 300) || r.status === 202
  );

  if (notasValidas.length === 0) {
    throw new Error("Nenhuma nota autorizada neste lote para download.");
  }

  // Se o lote tem exatamente 1 nota, faz download direto do arquivo .xml (mais rápido e prático)
  if (notasValidas.length === 1) {
    const notaData = notasValidas[0].data || {};
    const notaId = notaData.invoiceId || notaData.id;
    return await baixarXmlIndividual({
      notaId,
      xmlDireto: notaData.xml,
      dadosNota: notaData,
      baseUrl,
    });
  }

  const zip = new JSZip();
  const itensRelatorio = [];
  const total = notasValidas.length;

  onProgress({ current: 0, total, percent: 0, status: "Iniciando download do lote..." });

  for (let i = 0; i < total; i++) {
    const nota = notasValidas[i];
    const notaData = nota.data || {};
    const notaId = notaData.invoiceId || notaData.id;

    onProgress({
      current: i + 1,
      total,
      percent: Math.round(((i + 1) / total) * 90),
      status: `Baixando XML ${i + 1} de ${total}...`,
    });

    try {
      let xmlContent = notaData.xml;
      if (!xmlContent && notaId) {
        xmlContent = await obterXmlViaApi(notaId, baseUrl);
      }

      if (xmlContent) {
        const meta = extrairMetadadosXml(xmlContent, notaId);
        zip.file(meta.nomeArquivo, xmlContent);
        itensRelatorio.push({
          ...meta,
          status: "Autorizada SEFAZ",
        });
      } else {
        itensRelatorio.push({
          numero: `Nota_${i + 1}`,
          chave: notaId || "ERRO",
          status: "XML Indisponível",
          nomeArquivo: `Falha_Nota_${i + 1}.txt`,
        });
      }
    } catch (err) {
      console.warn(`Erro ao baixar nota ${i + 1}:`, err);
      itensRelatorio.push({
        numero: `Nota_${i + 1}`,
        chave: notaId || "ERRO",
        status: `Erro: ${err.message}`,
        nomeArquivo: `Erro_Nota_${i + 1}.txt`,
      });
    }
  }

  // Adiciona a planilha de resumo contábil para o contador
  const csvResumo = gerarCsvResumo(itensRelatorio);
  zip.file("resumo_lote_notas.csv", csvResumo);

  onProgress({ current: total, total, percent: 95, status: "Compactando arquivo .ZIP..." });

  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  const dataLote = lote.dataEmissao?.toDate
    ? lote.dataEmissao.toDate()
    : new Date();
  const dataFormatada = dataLote.toISOString().split("T")[0];
  const horaFormatada = dataLote.toTimeString().split(" ")[0].replace(/:/g, "h");
  const nomeZip = `Lote_NFCe_${dataFormatada}_${horaFormatada}.zip`;

  dispararDownloadBlob(zipBlob, nomeZip);
  onProgress({ current: total, total, percent: 100, status: "Download concluído!" });

  return { success: true, nomeArquivo: nomeZip, totalNotas: itensRelatorio.length };
};

/**
 * Empacota TODOS os XMLs de múltiplos lotes (período filtrado) em um único arquivo .ZIP consolidado.
 */
export const baixarPeriodoZip = async (lotes, nomePeriodo = "Consolidado", baseUrl, onProgress = () => {}) => {
  // 1. Extrai todas as notas válidas de todos os lotes do período
  const todasNotas = [];
  (lotes || []).forEach((lote) => {
    const notasLote = (lote.retornoNotaaS || []).filter(
      (r) => (r.status >= 200 && r.status < 300) || r.status === 202
    );
    notasLote.forEach((nota) => {
      todasNotas.push({
        nota,
        loteId: lote.id,
        dataEmissaoLote: lote.dataEmissao,
      });
    });
  });

  if (todasNotas.length === 0) {
    throw new Error("Nenhuma nota autorizada encontrada no período selecionado.");
  }

  // De-duplicar por ID caso haja repetições
  const notasUnicas = [];
  const idsVistos = new Set();
  todasNotas.forEach((item) => {
    const nData = item.nota.data || {};
    const id = nData.invoiceId || nData.id;
    if (id && !idsVistos.has(id)) {
      idsVistos.add(id);
      notasUnicas.push(item);
    } else if (!id) {
      notasUnicas.push(item);
    }
  });

  const zip = new JSZip();
  const itensRelatorio = [];
  const total = notasUnicas.length;

  onProgress({ current: 0, total, percent: 0, status: `Iniciando consolidação de ${total} notas...` });

  // Busca concorrente controlada (lotes de 4 requisições por vez para não sobrecarregar)
  const CONCURRENCY = 4;
  for (let i = 0; i < total; i += CONCURRENCY) {
    const chunk = notasUnicas.slice(i, i + CONCURRENCY);

    await Promise.all(
      chunk.map(async (item, chunkIdx) => {
        const globalIdx = i + chunkIdx;
        const notaData = item.nota.data || {};
        const notaId = notaData.invoiceId || notaData.id;

        try {
          let xmlContent = notaData.xml;
          if (!xmlContent && notaId) {
            xmlContent = await obterXmlViaApi(notaId, baseUrl);
          }

          if (xmlContent) {
            const meta = extrairMetadadosXml(xmlContent, notaId);
            zip.file(meta.nomeArquivo, xmlContent);
            itensRelatorio.push({
              ...meta,
              status: "Autorizada SEFAZ",
            });
          } else {
            itensRelatorio.push({
              numero: `Nota_${globalIdx + 1}`,
              chave: notaId || "ERRO",
              status: "XML Indisponível",
              nomeArquivo: `Falha_Nota_${globalIdx + 1}.txt`,
            });
          }
        } catch (err) {
          console.warn(`Erro ao baixar nota ${globalIdx + 1}:`, err);
          itensRelatorio.push({
            numero: `Nota_${globalIdx + 1}`,
            chave: notaId || "ERRO",
            status: `Erro: ${err.message}`,
            nomeArquivo: `Erro_Nota_${globalIdx + 1}.txt`,
          });
        }
      })
    );

    const processados = Math.min(i + CONCURRENCY, total);
    onProgress({
      current: processados,
      total,
      percent: Math.round((processados / total) * 90),
      status: `Baixando XMLs: ${processados} de ${total}...`,
    });
  }

  // Ordena itens do relatório por número da nota
  itensRelatorio.sort((a, b) => {
    const numA = Number(a.rawNumero || a.numero || 0);
    const numB = Number(b.rawNumero || b.numero || 0);
    return numA - numB;
  });

  // Adiciona a planilha CSV consolidada
  const csvResumo = gerarCsvResumo(itensRelatorio);
  zip.file(`resumo_fiscal_${nomePeriodo.toLowerCase().replace(/\s+/g, "_")}.csv`, csvResumo);

  onProgress({ current: total, total, percent: 95, status: "Gerando arquivo ZIP consolidado..." });

  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  const hoje = new Date().toISOString().split("T")[0];
  const nomeZip = `NFCe_Ecoparque_${nomePeriodo.replace(/\s+/g, "_")}_${hoje}.zip`;

  dispararDownloadBlob(zipBlob, nomeZip);
  onProgress({ current: total, total, percent: 100, status: "Exportação concluída com sucesso!" });

  return { success: true, nomeArquivo: nomeZip, totalNotas: itensRelatorio.length };
};
